import type { TSerial } from '@apps/definitions'

import { isValidSerial } from '@apps/helpers/isValidSerial'

import { DateTime } from '../DateTime/DateTime'
import { Semver } from '../Semver/Semver'
import type { TLanguage } from '../i18n/definitions'

import isValidRegionCode from '../i18n/v1/isValidRegionCode'
import { Object } from '../Object/Object'
import { Number } from '../Number/Number'
import { Array } from '../Array/Array'
import { is } from '../is/is'
import { RX } from '../RX/RX'

export default class Validator {
  public static isFunction     = is.function
  public static isBoolean      = is.boolean
  public static isDefined      = is.defined
  public static isNull         = is.null
  public static isFloat        = is.float
  public static isInteger      = is.integer
  public static isArray        = is.array
  public static isString       = is.string
  public static isNumber       = is.number
  public static isNumeric      = is.numeric
  public static isIncludes     = is.includes
  public static isNullable     = is.nullable
  public static isInstanceOf   = is.instanceOf
  public static toHaveProperty = is.property
  public static isTruthy       = is.truthy
  public static isFalsy        = is.falsy
  public static isEmpty        = is.empty
  public static isEven         = is.even
  public static isUndefined    = is.undefined
  public static isProperty     = is.property

 /**
  * A megadott paraméter biztosan object?
  * @param param - Az ismeretlen paraméter.
  */
  public static isObject <T extends TObject = Object> (param: unknown): param is T {
    return is.object(param)
  }

  /**
   * A megadott paraméter csak stringeket tartalmazó tömb?
   * @param param - Az ismeretlen paraméter.
   */
  public static isStringArray (param: unknown): param is string[] {
    return (
      Validator.isNonEmptyArray(param) &&
      param.every((str) => Validator.isString(str))
    )
  }

  /**
   * A megadott paraméter csak számokat tartalmazó tömb?
   * @param param - Az ismeretlen paraméter.
   */
  public static isNumberArray (param: unknown): param is number[] {
    return (
      Validator.isNonEmptyArray(param) &&
      param.every((str) => Validator.isNumber(str))
    )
  }

  /**
   * Ez egy shorthand az isObject függvényre, hogy ne kelljen
   * genericben átadni annak a típust.
   * @param param - Az ismeretlen paraméter.
   */
  public static isAnyObject (param: unknown): param is TAnyObject {
    return Validator.isObject<TAnyObject>(param)
  }

  /**
   * A megadott paraméter biztosan egy olyan string, ami tartalmaz szöveget.
   * @param param - Az ismeretlen paraméter.
   */
  public static isNonEmptyString (param: unknown): param is string {
    return (
      Validator.isString(param) &&
      !is.empty(param.trim())
    )
  }

  /**
   * A megadott paraméter trimmelt, szöveget tartalmazó string.
   * @param param - Az ismeretlen paraméter.
   */
  public static isNonEmptyTrimmedString (param: unknown): param is string {
    return (
      Validator.isNonEmptyString(param) &&
      param.trim() === param
    )
  }

  /**
   * Visszaadja, hogy a megadott paraméter pozitív szám-e.
   * Overkillnek tűnik, de amikor egy ismeretlen object property-jét passzoljuk át,
   * akkor hasznos.
   * @param param - Az ismeretlen paraméter.
   */
  public static isPositiveNumber (param: unknown): param is number {
    return Validator.isNumber(param) && param > 0
  }

  /**
   * Megvizsgálja, hogy a megadott paraméter numerikus értéket tartalmaz-e és az pozitív-e.
   * @param param - Az ismeretlen paraméter.
   */
  public static isPositiveNumeric (param: unknown): param is (number | string) {
    return Validator.isNumeric(param) && (Number.parse(param) > 0)
  }

  /**
   * A megadott paraméter biztosan egy olyan tömb, amiben vannak elemek.
   * @param param - Az ismeretlen paraméter.
   */
  public static isNonEmptyArray <T = unknown> (param: unknown): param is TNonEmptyArray<T> {
    return Validator.isArray(param) && !is.empty(param)
  }

  /**
   * A megadott paraméter biztosan egy olyan tömb, ami üres.
   * @param param - Az ismeretlen paraméter.
   */
  public static isEmptyArray <T = unknown> (param: unknown): param is TNonEmptyArray<T> {
    // Itt ne használjuk az is.empty -t, mert az nem tömb esetén is false-t dob!
    return Validator.isArray(param) && is.empty(param)
  }

  /**
   * Visszaadja, hogy a megadott paraméter egy object, aminek vannak kulcsai.
   * @param param - Az ismeretlen paraméter.
   */
  public static isNonEmptyObject <T extends TObject = TAnyObject> (param: unknown): param is T {
    return (
      Validator.isObject<T>(param) &&
      !is.empty(Object.getKeys(param))
    )
  }

  /**
   * Visszaadja, hogy az adott tömbben csak valid stringek vannak.
   * @see isNonEmptyString
   * @param param - Az ismeretlen paraméter.
   */
  public static isNonEmptyValidStringArray (param: unknown): param is TNonEmptyArray<string> {
    return (
      Validator.isNonEmptyArray(param) &&
      param.every(Validator.isNonEmptyString)
    )
  }

  /**
   * Leviszgálja, hogy a megadott `object`-nek van-e `keys` kulcsai.
   * @param param - Az ismeretlen paraméter.
   * @param keys  - Az `object` vizsgálandó kulcsai.
   */
  public static isObjectHaveKeys <T extends string | number | symbol> (
    param: unknown,
    keys: Readonly<TNonEmptyArray<T>>
  ): param is Partial<Record<T, unknown>> {
    if (!Validator.isNonEmptyObject(param)) {
      return false
    }

    const objKeys = Object.getKeys(param)

    return (
      keys.every((key) => Array.includes(objKeys, key))
    )
  }

  /**
   * Megvizsgálja, hogy a megadott `object` valóban object-e, és csak a `keys` kulcsokat tartalmazza.
   * @param param - Az ismeretlen paraméter.
   * @param keys  - Az Object kulcsai.
   */
  public static isObjectHaveJustKeys <
    T extends string | number | symbol,
    K = unknown
  > (
    param: unknown,
    keys: Readonly<TNonEmptyArray<T>>
  ): param is Record<T, K> {
    return (
      Validator.isNonEmptyObject(param) &&
      Validator.isObjectHaveKeys(param, keys) &&
      Object.getKeys(param).length === keys.length
    )
  }

  /**
   * Megvizsgálja, hogy egy adott sztring email formátumú e.
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidEmail (param: unknown): param is string {
    return Validator.isNonEmptyTrimmedString(param) && RX.EMAIL.test(param)
  }

  /**
   * Visszaadja, hogy a megadott érték szerepel-e a felsoroltak között.
   * Akkor érdemes ezt használni, ha nem egy meglévő tömbben akarunk keresni, hanem paramétereket dobálunk át.
   * @param param - Az ismeretlen paraméter.
   * @param list  - Az a lista, amiben keressük, hogy van-e egyezés.
   */
  public static isOneOf <T> (param: unknown, ...list: T[]): param is T {
    return Array.includes(list, param)
  }

  /**
   * Megvizsgálja, hogy YYYY-mm-dd formátumnak megfelelő-e a megadott paraméter.
   * @example
   * 2020-01-01
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidDate (param: unknown): param is string {
    return Validator.isString(param) && RX.DATE.test(param)
  }

  /**
   * Megvizsgálja, hogy a megadott paraméter:
   * - megfelelő formátumú (YYYY-MM-DD), és
   * - jövőbeli dátum (a mai napnál későbbi).
   * @example
   * 2099-12-31 -- true
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidFutureDate (param: unknown): param is string {
    if (!Validator.isValidDate(param)) {
      return false
    }

    const today = DateTime.getCurrentDate()
    const inputDate = DateTime.getDate(param)

    // Csak akkor érvényes, ha későbbi nap, mint a mai nap
    return DateTime.getDiffMs(inputDate, today) > 0
  }

  /**
   * Megvizsgálja, hogy ISO YYYY-mm-dd hh:ii:ss formátumnak megfelelő-e a megadott paraméter.
   * @example
   * 2020-01-01 20:15:30
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidISODate (param: unknown): param is string {
    return Validator.isString(param) && RX.ISO_DATE.test(param)
  }

  /**
   * Megvizsgálja, hogy ISO YYYY-mm-ddThh:ii:ss formátumnak megfelelő-e a megadott paraméter.
   * A különbség a sima ISO validálás között, hogy itt a "T" szeparátornak is jelen kell lennie.
   * @example
   * 2020-01-01T20:15:30
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidISODateWithT (param: unknown): param is string {
    return Validator.isString(param) && RX.ISO_DATE_WITH_T.test(param)
  }

  /**
   * Megvizsgálja, hogy bármilyen általunk támogatott dátumra illeszkedik-e a paraméter.
   * @example
   * 2020-01-01
   * 2020-01-01 00:00:00
   * 2020-01-01T00:00:00
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidAnyDate (param: unknown): param is string {
    return (
      Validator.isValidDate(param) ||
      Validator.isValidISODate(param) ||
      Validator.isValidISODateWithT(param)
    )
  }

  /**
   * Visszaadja, hogy a megadott paraméter valóban Ügyesedni szériaszám-e.
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidUgyesedniSerial (param: unknown): param is TSerial {
    return isValidSerial(param)
  }

  /**
   * Levizsgálja, hogy a megadott `value` érték tömb és minden eleme ügyesedni serial.
   * @param param - A vizsgálandó érték.
   */
  public static isValidUgyesedniSerials (param: unknown): param is TSerial[] {
    return Validator.isArray(param) && param.every(Validator.isValidUgyesedniSerial)
  }

  /**
   * Visszaadja, hogy a megadott paraméter értéke megfelel-e valamelyik nyelvnek.
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidLanguage (param: unknown): param is TLanguage {
    if (!Validator.isString(param)) {
      return false
    }

    return isValidRegionCode(param)
  }

  /**
   * A megadott paraméter valóban base64 string?
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidBase64ImageString (param: unknown): param is string {
    if (!Validator.isString(param)) {
      return false
    }

    return RX.BASE64IMAGE.test(param)
  }

  /**
   * Megmondja, hogy a vizsgált paraméter egy paginator objektum-e.
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidPaginator (param: unknown): param is { actPage: number, itemsPerPage: number } {
    const isValid = (
      Validator.isAnyObject(param) &&
      Validator.isObjectHaveKeys(param, [ 'itemsPerPage', 'actPage' ]) &&
      Validator.isNumber(param.actPage) &&
      Validator.isNumber(param.itemsPerPage)
    )

    return isValid
  }

  /**
   * Megmondja, hogy az adot paraméter szám-e és a megadott rangen belül van.
   * @param param - Az ismeretlen paraméter.
   * @param min   - A minimális érték.
   * @param max   - A maximális érték.
   */
  public static isNumberBetween (param: unknown, min: number, max: number): param is number {
    return (
      Validator.isNumber(param) &&
      param >= min &&
      param <= max
    )
  }

  /**
   * A vizsgált string hosszúsága beesik az átadott intervallumba.
   * @param param - Vizsgált string.
   * @param min   - Min érték.
   * @param max   - Max érték.
   */
  public static isStringLengthBetween (param: unknown, min: number, max: number): param is string {
    return (
      // NE empty stringre vizsgálj!
      Validator.isString(param) &&
      Validator.isNumberBetween(param.length, min, max)
    )
  }

  /**
   * Megmondja egy ismeretlen paraméterről, hogy az egy valid IP (v4) cím-e.
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidIpV4Address (param: unknown): param is string {
    return (
      Validator.isNonEmptyString(param) &&
      RX.IPV4.test(param)
    )
  }

  /**
   * Megmondja egy ismeretlen paraméterről, hogy az egy év-e.
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidYear (param: unknown): param is string {
    return (
      Validator.isNonEmptyString(param) &&
      RX.YEAR.test(param)
    )
  }

  /**
   * Megmondja, hogy a paraméter true vagy 1.
   * Azért hasznos ez a függvény, mert ha időközben változik vagy számról booleanre vagy fordítva, akkor is hibatűrő marad.
   * @param param - Az ismeretlen paraméter.
   */
  public static isCheckboxCheckedValue (param: unknown): param is 1 | true {
    return Validator.isOneOf< 1 | true>(param, 1, true)
  }

  /**
   * Megmondja, hogy a paraméter valamilyen checkbox értéket tartalmaz-e.
   * Azért hasznos ez a függvény, mert ha időközben változik vagy számról booleanre vagy fordítva, akkor is hibatűrő marad.
   * @param param - Az ismeretlen paraméter.
   */
  public static isCheckboxValue (param: unknown): param is 1 | true {
    return Validator.isOneOf< 1 | 0 | true | false>(param, 1, 0, true, false)
  }

  /**
   * A megadott paraméter semver-nek megfelel?
   * @param param - Az ismeretlen paraméter.
   */
  public static isValidSemver (param: unknown): param is string {
    return Semver.isValid(param)
  }
}
